import { type ReactElement, useCallback } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { format } from 'date-fns'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { i18next } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { AnimatedValue, Badge } from '@/shared/components/ui'
import { formatMoney } from '@/shared/utils/money'
import CostBadge from '@/features/payments/components/CostBadge'
import PaymentInvoiceBadge from '@/features/payments/components/PaymentInvoiceBadge'
import LinkedCostsPopover from '@/features/payments/components/LinkedCostsPopover'
import PaymentRequirementsPopover from '@/features/payments/components/PaymentRequirementsPopover'
import PaymentVatPopover from '@/features/payments/components/PaymentVatPopover'
import type { PaymentDTO } from '@/features/payments/schemas/paymentsSchema'

interface PaymentCardProps {
  index: number
  payment: PaymentDTO
  isDualColumn: boolean
  canDragPayments: boolean
  canDropInvoices: boolean
  isDragActive: boolean
  showSatisfactionStatus: boolean
  showInvoiceStatus: boolean
  recentlyLinked: boolean
  onEditInvoice: (invoiceUuid: string) => Promise<void> | void
}

export function PaymentCard({
  index,
  payment,
  isDualColumn,
  canDragPayments,
  canDropInvoices,
  isDragActive,
  showSatisfactionStatus,
  showInvoiceStatus,
  recentlyLinked,
  onEditInvoice,
}: PaymentCardProps): ReactElement {
  const { t } = useTranslation('features-payments')
  const isCredit = payment.direction === 'CREDIT'
  const canDrag = canDragPayments && !isCredit
  const canDropInvoice = canDropInvoices && !isCredit

  const { attributes, listeners, setNodeRef: setDragNodeRef, transform, isDragging } = useDraggable({
    id: `payment-${payment.id}`,
    data: { type: 'payment', paymentId: payment.id },
    disabled: !canDrag,
  })
  const { isOver, setNodeRef: setDropNodeRef } = useDroppable({
    id: `payment-target-${payment.id}`,
    data: { type: 'payment-target', paymentId: payment.id },
    disabled: !canDropInvoice,
  })
  const setNodeRef = useCallback(
    (node: HTMLElement | null) => {
      setDragNodeRef(node)
      setDropNodeRef(node)
    },
    [setDragNodeRef, setDropNodeRef],
  )

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined

  const dateStr = format(new Date(payment.txnDate), 'dd MMM')
  const hasLinkedCosts = (payment.linkedIndirectCostCount ?? 0) > 0
  const linkedSubcategories = payment.linkedSubcategories ?? []
  const primaryLinkedSubcategory = linkedSubcategories[0]
  const extraLinkedCount = Math.max(
    0,
    (payment.linkedIndirectCostCount ?? linkedSubcategories.length) -
      (primaryLinkedSubcategory ? 1 : 0),
  )
  const showLinkedButtonFallback = hasLinkedCosts && !primaryLinkedSubcategory
  const linkingRequirementSatisfied =
    payment.linkingRequirementSatisfied === true ||
    payment.costLinkingRequirement === 'NOT_NEEDED' ||
    (payment.linkedIndirectCostCount ?? 0) > 0
  const vatSet =
    payment.vatApplicable !== null &&
    (payment.vatApplicable === false ||
      typeof payment.vatRate === 'number' ||
      payment.reverseCharge === true)
  const rowSatisfied = linkingRequirementSatisfied && vatSet
  const invoiceSatisfied =
    payment.invoiceStatus === 'INVOICE_PRESENT' ||
    payment.invoiceStatus === 'INVOICE_NOT_NEEDED'
  const primaryLabel = payment.counterpartyName?.trim() || payment.title?.trim() || '—'
  const titleLabel = payment.title?.trim() || ''
  const showTitleInMeta = titleLabel.length > 0 && titleLabel !== primaryLabel
  const showInvoiceBadge = payment.invoiceStatus === 'INVOICE_PRESENT'
  const showInvoiceNotNeededBadge = payment.invoiceStatus === 'INVOICE_NOT_NEEDED'
  const amountInCents = Math.round(payment.signedAmount * 100)

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(canDrag ? { ...listeners, ...attributes } : {})}
      className={cn(
        'rounded-xl border bg-card transition-[border-color,box-shadow,background-color,transform,opacity] duration-200 ease-out',
        canDropInvoice && isOver && 'ring-2 ring-primary/60 border-primary/40 bg-primary/5 scale-[1.01]',
        canDropInvoice && isDragActive && !isOver && 'ring-1 ring-muted-foreground/20',
        'hover:border-foreground/35 hover:ring-1 hover:ring-foreground/15 hover:shadow-[0_10px_28px_rgba(0,0,0,0.26)]',
        canDrag && 'cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-40 shadow-lg z-50',
        isCredit && 'opacity-60',
        !isOver && 'border-border',
      )}
    >
      <div className="flex items-stretch">
        <div
          className={cn(
            'relative flex w-12 shrink-0 items-center justify-center border-r border-border/40 bg-muted/10 transition-colors',
            showSatisfactionStatus && rowSatisfied && 'bg-emerald-500/12',
            showInvoiceStatus && invoiceSatisfied && 'bg-blue-500/12',
            recentlyLinked &&
              'bg-emerald-500/14 ring-1 ring-inset ring-emerald-400/25',
          )}
        >
          <span
            className={cn(
              'text-base font-semibold tabular-nums text-muted-foreground/35 transition-all duration-200',
              recentlyLinked && 'scale-90 opacity-0',
            )}
          >
            {index}
          </span>
          <Check
            className={cn(
              'pointer-events-none absolute size-4 text-[var(--border-positive)] transition-all duration-200',
              recentlyLinked ? 'scale-100 opacity-100' : 'scale-90 opacity-0',
            )}
            aria-hidden="true"
          />
        </div>
        <div className="flex flex-1 items-center justify-between gap-4 min-w-0 p-4">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <p
                className={cn(
                  'truncate min-w-0 w-fit shrink text-[1.02rem] font-semibold leading-snug text-foreground',
                  isDualColumn ? 'max-w-[28ch]' : 'max-w-[44ch]',
                )}
                title={primaryLabel}
              >
                {primaryLabel}
              </p>
              {primaryLinkedSubcategory ? (
                <CostBadge
                  paymentId={payment.id}
                  costUuid={primaryLinkedSubcategory.costUuid}
                  displayName={primaryLinkedSubcategory.displayName}
                />
              ) : null}
              {extraLinkedCount > 0 ? (
                <Badge variant="secondary" className="shrink-0 text-[11px]">
                  +{extraLinkedCount}
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground/80">
              <span className="shrink-0 text-muted-foreground/55">{dateStr}</span>
              {showTitleInMeta ? (
                <>
                  <span className="text-muted-foreground/45">|</span>
                  <span
                    className={cn(
                      'truncate text-muted-foreground/75',
                      isDualColumn ? 'max-w-[26ch]' : 'max-w-[40ch]',
                    )}
                    title={titleLabel}
                  >
                    {titleLabel}
                  </span>
                </>
              ) : null}
              {showInvoiceBadge ? (
                <PaymentInvoiceBadge
                  paymentId={payment.id}
                  onEditInvoice={onEditInvoice}
                  className="shrink-0"
                />
              ) : null}
              {showInvoiceNotNeededBadge ? (
                <Badge
                  variant="secondary"
                  className="h-5 shrink-0 gap-1 px-1.5 text-[10px] text-muted-foreground/80"
                >
                  {t('requirementsPopover.invoiceNotNeeded')}
                </Badge>
              ) : null}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <span
              className={cn(
                'text-sm font-semibold tabular-nums tracking-tight',
                isCredit ? 'text-emerald-400/90' : 'text-foreground/80',
              )}
            >
              <AnimatedValue
                value={amountInCents}
                formatter={value =>
                  formatMoney(value / 100, payment.currency, i18next.language, 2, 2, 'narrowSymbol', true)
                }
                duration={220}
                easing="easeOut"
                blur
                blurDuration={150}
              />
            </span>
            <div
              className="flex items-center gap-1.5 opacity-90"
              onPointerDown={e => e.stopPropagation()}
            >
              {showLinkedButtonFallback && (
                <LinkedCostsPopover
                  payment={payment}
                  quiet
                  className="h-7 min-w-0 border-transparent bg-transparent px-1.5 text-muted-foreground/70 hover:text-muted-foreground hover:border-border/50 hover:bg-muted/15"
                />
              )}
              <PaymentVatPopover
                payment={payment}
                quiet
                className="h-7 min-w-0 border-transparent bg-transparent px-1.5 text-muted-foreground/80 hover:text-muted-foreground hover:border-border/50 hover:bg-muted/15"
              />
              <PaymentRequirementsPopover payment={payment} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
